"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Wrench, Clock, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const features = [{
  id: 1,
  title: "Genuine Factory Parts",
  description: "Original American Standard flappers, fill valves, and seal kits."
}, {
  id: 2,
  title: "24/7 Emergency Response",
  description: "Fast arrival across Jonesboro and nearby areas."
}, {
  id: 3,
  title: "Champion & Cadet Specialists",
  description: "Expert diagnostics on standard, dual-flush, and high-efficiency models."
}];
export default function AmericanHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="americanstandardhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border">
      {/* Decorative background grid and ambient glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(215_85%_22%_/_0.06),transparent_50%)] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main content column */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <ShieldCheck className="size-3.5 text-primary mr-1" />
                Brand-Certified Plumbing Service
              </Badge>
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Clock className="size-3.5 text-accent-foreground" />
                24/7 Available in Jonesboro, GA
              </span>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                American Standard Toilet Repair & Installation
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl font-default">
                Stop persistent running water, weak flushes, and leaks with specialized American Standard toilet service. Our licensed Jonesboro plumbers arrive fully equipped with authentic replacement parts.
              </p>
            </motion.div>

            {/* CTA action buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold">
                <Link to="/contact/">
                  <PhoneCall className="mr-2 size-5" />
                  Request Emergency Service
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-semibold">
                <Link to="/service-areas/">
                  View Service Areas
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Trust feature points */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border">
              {features.map((feature, i) => <div key={feature.id} data-index={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{feature.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    {feature.description}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Media and card visual column */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          scale: shouldReduceMotion ? 1 : 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Primary image container */}
              <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/american_standard_toilet_repair.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/american_standard_toilet_repair.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/american_standard_toilet_repair.png-wvc-sizes" alt="Plumber servicing an American Standard toilet in a modern bathroom" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover object-center" />
                <div className="absolute inset-0 bg-linear-to-t from-foreground/70 via-transparent to-transparent pointer-events-none" />
                
                <div className="absolute bottom-4 left-4 right-4 text-background p-4 rounded-xl bg-foreground/60 backdrop-blur-xs border border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-white/80 uppercase tracking-wider">Common Solution</p>
                      <p className="text-sm font-bold text-white">Champion 4 & Cadet 3 Flush Tower Rebuilds</p>
                    </div>
                    <Wrench className="size-6 text-white shrink-0" />
                  </div>
                </div>
              </div>

              {/* Floating emergency badge card */}
              <Card className="absolute -bottom-6 -left-4 sm:-left-6 max-w-[240px] bg-card/95 backdrop-blur-sm border-border shadow-lg p-0">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                    <PhoneCall className="size-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">Fast Dispatch</span>
                    <span className="text-sm font-bold text-foreground">Jonesboro, GA</span>
                  </div>
                </CardContent>
              </Card>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}